import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiHome, FiList, FiMapPin } from 'react-icons/fi';
import { LuBed, LuBath, LuRuler } from 'react-icons/lu';

import Header from '../components/Header';
import Footer from '../components/layout/Footer';
import Seo, { organizationSchema } from '../components/Seo';
import { Container, Select, Skeleton } from '../components/ui';
import PropertySearchMap from '../components/property/PropertySearchMap';
import {
  MapBounds,
  MapPin,
  mainCityOf,
  pinsWithin,
} from '../components/property/propertyClusters';
import { propertyService, buildImageUrl } from '../services/api';
import { useSettings } from '../contexts/SettingsContext';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { translatePropertyType } from '../utils/translations';

const Page = styled.div`
  background: ${({ theme }) => theme.colors.background};
  min-height: 60vh;
`;

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 0;

  /* Os selects são filtros, não campos de formulário: ocupam o que precisam. */
  select { flex: 0 1 200px; }
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.5rem;
  margin: 0;
  flex: 1 1 260px;
  color: ${({ theme }) => theme.colors.foreground};
`;

const ListLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 600;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: 1.45fr 1fr;
  gap: 1.25rem;
  height: calc(100vh - 220px);
  min-height: 520px;
  padding-bottom: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const MapArea = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: 900px) {
    height: 420px;
  }
`;

const ListArea = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ListHead = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  padding-bottom: 0.6rem;
`;

const Cards = styled.div`
  display: grid;
  gap: 0.75rem;
  overflow-y: auto;
  padding-right: 0.35rem;

  @media (max-width: 900px) {
    max-height: none;
    overflow: visible;
  }
`;

const Card = styled.article<{ $active: boolean }>`
  display: grid;
  grid-template-columns: 112px 1fr;
  gap: 0.85rem;
  padding: 0.6rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.card};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }

  img {
    width: 112px;
    height: 84px;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    background: ${({ theme }) => theme.colors.muted};
  }
`;

/* Imóvel sem foto existe (cadastro recém-criado, importação em andamento):
   melhor um espaço identificável que um arquivo quebrado. */
const NoPhoto = styled.div`
  display: grid;
  place-items: center;
  width: 112px;
  height: 84px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.muted};
  color: ${({ theme }) => theme.colors.neutral[400]};
`;

const CardBody = styled.div`
  min-width: 0;

  h3 {
    margin: 0 0 0.2rem;
    font-size: 0.9rem;
    line-height: 1.3;
    color: ${({ theme }) => theme.colors.foreground};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const Price = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.95rem;
`;

const Specs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.3rem;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.text.secondary};

  span { display: inline-flex; align-items: center; gap: 0.25rem; }
`;

const Where = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

const Empty = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9rem;
`;

/** Centro geográfico do Brasil: só entra em cena quando o tenant não tem nem
 *  imóvel com ponto nem endereço configurado -- aí qualquer escolha seria
 *  chute, e um chute no país certo é o menos pior. */
const BRAZIL_CENTER: [number, number] = [-15.78, -47.93];

const formatPrice = (value?: number) =>
  typeof value === 'number'
    ? new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      }).format(value)
    : 'Consulte';

const PropertyMapPage: React.FC = () => {
  useScrollToTop();
  const { siteSettings } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pins, setPins] = useState<MapPin[]>([]);
  const [loading, setLoading] = useState(true);
  const [bounds, setBounds] = useState<MapBounds | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const type = searchParams.get('type') || '';
  const transactionType = searchParams.get('transaction_type') || '';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    propertyService
      .getMapPins({
        type: type || undefined,
        transaction_type: transactionType || undefined,
      })
      .then(result => {
        if (!cancelled) setPins(result);
      })
      .catch(error => console.error('Erro ao carregar os imóveis do mapa:', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type, transactionType]);

  // A lista acompanha o mapa: o que está fora da janela não está sendo
  // procurado. É o que faz "arrastar o mapa" virar uma busca.
  const visible = useMemo(() => pinsWithin(pins, bounds), [pins, bounds]);

  // Nada aqui pode citar uma cidade fixa: a mesma tela serve qualquer
  // imobiliária da plataforma. A região sai do catálogo do tenant.
  const mainCity = useMemo(() => mainCityOf(pins), [pins]);
  const regionLabel = mainCity ? `${mainCity} e região` : 'toda a região atendida';

  const fallbackCenter = useMemo<[number, number]>(() => {
    const latitude = Number(siteSettings?.contact_map_latitude);
    const longitude = Number(siteSettings?.contact_map_longitude);
    return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude !== 0
      ? [latitude, longitude]
      : BRAZIL_CENTER;
  }, [siteSettings?.contact_map_latitude, siteSettings?.contact_map_longitude]);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const handleBounds = useCallback((next: MapBounds) => setBounds(next), []);

  return (
    <>
      <Seo
        title={`Buscar imóveis no mapa | ${
          siteSettings?.company_info?.name || siteSettings?.site_name || 'Imobiliária'
        }`}
        description={`Encontre imóveis pela localização: navegue pelo mapa de ${regionLabel} e veja o que está à venda e para alugar em cada bairro.`}
        canonicalPath="/mapa"
        structuredData={organizationSchema(siteSettings)}
      />
      <Header />
      <Page>
        <Container>
          <Bar>
            <Title>Imóveis no mapa</Title>
            <Select
              value={transactionType}
              onChange={event => updateFilter('transaction_type', event.target.value)}
              aria-label="Finalidade"
            >
              <option value="">Comprar ou alugar</option>
              <option value="venda">Comprar</option>
              <option value="aluguel">Alugar</option>
            </Select>
            <Select
              value={type}
              onChange={event => updateFilter('type', event.target.value)}
              aria-label="Tipo de imóvel"
            >
              <option value="">Todos os tipos</option>
              <option value="casa">Casa</option>
              <option value="apartamento">Apartamento</option>
              <option value="terreno">Terreno</option>
              <option value="comercial">Comercial</option>
              <option value="chacara">Chácara</option>
            </Select>
            <ListLink to="/imoveis">
              <FiList size={16} /> Ver em lista
            </ListLink>
          </Bar>

          <Split>
            <MapArea>
              {loading ? (
                <Skeleton style={{ height: '100%', width: '100%' }} />
              ) : (
                <PropertySearchMap
                  pins={pins}
                  selectedId={selectedId}
                  onSelect={pin => setSelectedId(pin.id)}
                  onBoundsChange={handleBounds}
                  fallbackCenter={fallbackCenter}
                />
              )}
            </MapArea>

            <ListArea>
              <ListHead>
                {loading
                  ? 'Carregando imóveis...'
                  : `${visible.length} ${
                      visible.length === 1 ? 'imóvel nesta área' : 'imóveis nesta área'
                    }`}
              </ListHead>
              <Cards>
                {visible.map(pin => (
                  <Card
                    key={pin.id}
                    $active={pin.id === selectedId}
                    onClick={() => setSelectedId(pin.id)}
                  >
                    {pin.cover_image ? (
                      <img src={buildImageUrl(pin.cover_image)} alt={pin.title} loading="lazy" />
                    ) : (
                      <NoPhoto>
                        <FiHome size={22} />
                      </NoPhoto>
                    )}
                    <CardBody>
                      <Price>{formatPrice(pin.price)}</Price>
                      <h3>
                        <Link to={`/imovel/${pin.id}`}>{pin.title}</Link>
                      </h3>
                      <Specs>
                        {pin.bedrooms ? (
                          <span>
                            <LuBed size={13} /> {pin.bedrooms}
                          </span>
                        ) : null}
                        {pin.bathrooms ? (
                          <span>
                            <LuBath size={13} /> {pin.bathrooms}
                          </span>
                        ) : null}
                        {pin.area ? (
                          <span>
                            <LuRuler size={13} /> {pin.area} m²
                          </span>
                        ) : null}
                        <span>{translatePropertyType(pin.type)}</span>
                      </Specs>
                      <Where>
                        <FiMapPin size={12} /> {pin.neighborhood || pin.city || 'Localização aproximada'}
                      </Where>
                    </CardBody>
                  </Card>
                ))}
                {!loading && visible.length === 0 && (
                  <Empty>
                    Nenhum imóvel nesta área do mapa. Afaste o zoom ou arraste para outra região.
                  </Empty>
                )}
              </Cards>
            </ListArea>
          </Split>
        </Container>
      </Page>
      <Footer />
    </>
  );
};

export default PropertyMapPage;
